import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LandingFooter, LandingHeader } from '@/features/landing/LandingChrome';
import HeroSection from '@/features/landing/HeroSection';
import ProductTour from '@/features/landing/ProductTour';
import ModuleExplorer from '@/features/landing/ModuleExplorer';
import KennismakingSection from '@/features/landing/KennismakingSection';
import { FaqSection, WeekSection, WhySection } from '@/features/landing/LandingSections';
import { TOUR } from '@/features/landing/landingCopy';
import { useLandingSeo } from '@/features/landing/useLandingSeo';
import '@/features/landing/landing.css';

const TAB_BY_HASH = Object.fromEntries(TOUR.tabs.map((t) => [t.hash, t.key]));
const reducedMotion = () => !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Commercial landing page V3 for route "/". Copy: features/landing/landingCopy.js.
const LandingPage = () => {
  const { hash } = useLocation();
  const [tab, setTab] = useState('org');
  const focusTab = useRef(null);
  useLandingSeo();

  const selectTab = useCallback((key, { scroll = false, focus = false } = {}) => {
    setTab(key);
    focusTab.current = focus ? key : null;
    if (scroll) {
      document.getElementById('tour')?.scrollIntoView({ block: 'start', behavior: reducedMotion() ? 'auto' : 'smooth' });
    }
  }, []);

  // Keyboard focus follows the selected tab after the panels have switched.
  useEffect(() => {
    if (!focusTab.current) return;
    document.getElementById(`tab-${focusTab.current}`)?.focus({ preventScroll: true });
    focusTab.current = null;
  }, [tab]);

  // Deep links: /#kennismaking, /#demo (alias, for example from /pricing) and /#tour-organiseren|verbinden|creeren.
  useEffect(() => {
    if (!hash) return;
    const id = decodeURIComponent(hash.slice(1));
    if (TAB_BY_HASH[id]) {
      selectTab(TAB_BY_HASH[id], { scroll: true });
      return;
    }
    document.getElementById(id)?.scrollIntoView();
  }, [hash, selectTab]);

  return (
    <div className="zoea-landing">
      <a href="#main" className="zl-skip">Ga naar de inhoud</a>
      <LandingHeader />
      <main id="main" tabIndex={-1}>
        <HeroSection />
        <WhySection />
        <ModuleExplorer />
        <ProductTour tab={tab} onSelect={selectTab} />
        <WeekSection onSelectTab={selectTab} />
        <FaqSection />
        <KennismakingSection />
      </main>
      <LandingFooter />
    </div>
  );
};

export default LandingPage;
